<script setup>
import { computed, useTemplateRef } from 'vue';
import { useWindowSize, useElementBounding } from '@vueuse/core';

const props = defineProps({
  option: {
    type: Object,
    default: () => {},
  },
  subMenuAvailable: {
    type: Boolean,
    default: true,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
  // Aceleres 2026-09-02: acordeao no DESKTOP, a pedido. O submenu deixa de
  // ser um painel flutuante ao lado (aberto por hover) e passa a expandir
  // para baixo, dentro do proprio menu, como as seccoes do menu de filtros.
  // A mecanica ja existia para a folha mobile (`mobile`); esta prop reusa-a
  // sem trazer junto o desenho de 60px de altura e fundo transparente que a
  // folha usa. As duas nunca estao ligadas ao mesmo tempo.
  accordion: {
    type: Boolean,
    default: false,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle']);
const isAccordion = computed(() => props.mobile || props.accordion);
const isSubmenuOpen = computed(() => isAccordion.value && props.expanded);

const toggleSubmenu = () => {
  if (!isAccordion.value || !props.subMenuAvailable) return;
  emit('toggle', props.option.key);
};

// O menu de contexto do cartao fecha no `@blur` do proprio contentor
// (ContextMenu.vue) e um <button> ROUBA o foco ao ser clicado: com o
// acordeao no desktop, clicar numa seccao fechava o menu inteiro antes de
// ela chegar a expandir. Impedir a mudanca de foco no mousedown resolve — o
// clique completa na mesma. E a mesma correcao que o caixote de apagar
// etiqueta ja usa no menuItem.vue. Na folha mobile nao se aplica: ali o
// fecho e por toque fora, nao por blur, e tirar o foco do botao prejudicaria
// a navegacao por teclado.
const onTriggerMousedown = event => {
  if (props.accordion && !props.mobile) event.preventDefault();
};

// A maioria das linhas deste menu manda um nome de `fluent-icon` ('people',
// 'arrow-swap'). A linha de Classificacao do contato manda a classe Tailwind
// do mesmo icone que o menu do cabecalho usa ('i-lucide-...'), para os dois
// menus mostrarem o mesmo desenho. Sem esta distincao o `fluent-icon` recebe
// um nome que nao existe e a linha fica sem icone nenhum.
const isTailwindIcon = computed(
  () =>
    typeof props.option?.icon === 'string' && props.option.icon.startsWith('i-')
);

const menuRef = useTemplateRef('menuRef');
const { width: windowWidth, height: windowHeight } = useWindowSize();
const { bottom, right } = useElementBounding(menuRef);

// Vertical position
const verticalPosition = computed(() => {
  const SUBMENU_HEIGHT = 240; // 15rem in pixels
  const spaceBelow = windowHeight.value - bottom.value;
  return spaceBelow < SUBMENU_HEIGHT ? 'bottom-0' : 'top-0';
});

// Horizontal position
const horizontalPosition = computed(() => {
  const SUBMENU_WIDTH = 240;
  const spaceRight = windowWidth.value - right.value;
  return spaceRight < SUBMENU_WIDTH ? 'right-full' : 'left-full';
});

const submenuPosition = computed(() => [
  verticalPosition.value,
  horizontalPosition.value,
]);
</script>

<template>
  <div
    ref="menuRef"
    class="group/submenu menu-with-submenu relative flex w-full cursor-pointer justify-between rounded-md bg-n-alpha-3/50 px-2 py-1.5 text-n-slate-12 backdrop-blur-[100px] hover:bg-n-brand/10 dark:hover:bg-n-solid-3"
    :class="[
      !subMenuAvailable ? 'cursor-not-allowed opacity-50' : '',
      mobile
        ? 'min-h-[52px] min-w-0 flex-col items-stretch !rounded-none !bg-transparent !p-0 !backdrop-blur-none hover:!bg-transparent'
        : accordion
          ? 'min-h-9 min-w-60 flex-col items-stretch'
          : 'h-9 min-w-60 items-center',
    ]"
  >
    <component
      :is="isAccordion ? 'button' : 'div'"
      :type="isAccordion ? 'button' : undefined"
      class="flex w-full min-w-0 items-center justify-between"
      :class="{
        'min-h-[52px] border-0 bg-transparent px-3 text-left text-n-slate-12 active:bg-n-alpha-2':
          mobile,
        'border-0 bg-transparent p-0 text-left text-inherit':
          accordion && !mobile,
      }"
      :aria-expanded="isAccordion ? isSubmenuOpen : undefined"
      :tabindex="accordion && !mobile ? -1 : undefined"
      @mousedown="onTriggerMousedown"
      @click.stop="toggleSubmenu"
    >
      <div class="flex h-5 min-w-0 flex-1 items-center">
        <span
          v-if="isTailwindIcon"
          class="menu-icon flex-shrink-0"
          :class="[option.icon, mobile ? 'size-5' : 'size-4']"
        />
        <fluent-icon
          v-else
          :icon="option.icon"
          :size="mobile ? 20 : 16"
          class="menu-icon flex-shrink-0"
        />
        <p
          class="my-0 min-w-0 flex-1 truncate"
          :class="mobile ? 'mx-3 text-[15px]' : 'mx-2.5 text-sm'"
        >
          {{ option.label }}
        </p>
      </div>
      <!-- A seta de origem aponta para o lado porque o submenu era um painel
           flutuante ao lado. No acordeao ela aponta para BAIXO, como no menu
           de filtros, e vira 180 graus quando a seccao esta aberta. -->
      <fluent-icon
        :icon="isAccordion ? 'chevron-down' : 'chevron-right'"
        :size="mobile ? 18 : 14"
        class="flex-shrink-0 transition-transform"
        :class="{
          'rotate-180': isAccordion && isSubmenuOpen,
          'rotate-90': !isAccordion && isSubmenuOpen,
        }"
      />
    </component>
    <div
      v-if="subMenuAvailable"
      class="submenu min-w-0 cursor-pointer overflow-x-hidden overflow-y-auto overscroll-contain rounded-md bg-n-solid-2 p-1"
      :class="
        mobile
          ? [
              isSubmenuOpen ? 'block' : 'hidden',
              'relative w-full !max-h-none !overflow-x-visible !overflow-y-visible divide-y divide-n-weak/60 !rounded-none border-0 bg-transparent !p-0 shadow-none',
            ]
          : accordion
            ? [
                isSubmenuOpen ? 'block' : 'hidden',
                'relative mt-2 w-full max-h-[15rem]',
              ]
            : [
                submenuPosition,
                'absolute z-50 hidden max-h-[15rem] w-60 max-w-[calc(100vw-1rem)] shadow-lg group-hover/submenu:block',
              ]
      "
      @click.stop
    >
      <slot />
    </div>
  </div>
</template>
